import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, KeyRound, X, Inbox, Mail, MessageSquare, Star, Settings, Check, AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { adminApi, clearToken, getToken, formatApiError } from "@/lib/adminApi";

const TABS = [
  { key: "priority_list", label: "Priority List", icon: Star },
  { key: "inquiries", label: "Coaching Inquiries", icon: Inbox },
  { key: "subscribers", label: "Subscribers", icon: Mail },
  { key: "contacts", label: "Messages", icon: MessageSquare },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("priority_list");
  const [stats, setStats] = useState({ priority_list: 0, inquiries: 0, subscribers: 0, contacts: 0 });
  const [data, setData] = useState({ priority_list: [], inquiries: [], subscribers: [], contacts: [] });
  const [showPw, setShowPw] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const logout = useCallback(() => {
    clearToken();
    navigate("/admin/login");
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, pri, inq, sub, con] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/priority-list"),
        adminApi.get("/admin/inquiries"),
        adminApi.get("/admin/subscribers"),
        adminApi.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setData({ priority_list: pri.data, inquiries: inq.data, subscribers: sub.data, contacts: con.data });
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      } else {
        toast.error("Could not load data.");
      }
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        await adminApi.get("/auth/me");
        setReady(true);
        await loadAll();
      } catch {
        logout();
      }
    })();
  }, [navigate, logout, loadAll]);

  const exportCsv = async (kind) => {
    try {
      const res = await adminApi.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `thriveandmother_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch {
      toast.error("Export failed.");
    }
  };

  if (!ready) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream" data-testid="admin-loading">
        <p className="font-serif text-2xl text-wine animate-pulse">Loading…</p>
      </main>
    );
  }

  const rows = data[tab];

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-gold/40 bg-cream/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Wordmark size="sm" />
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSetup(true)}
              data-testid="admin-setup-open"
              className="inline-flex items-center gap-2 text-sm text-charcoal hover:text-terracotta transition-colors"
            >
              <Settings size={16} /> <span className="hidden sm:inline">Setup</span>
            </button>
            <button
              onClick={() => setShowPw(true)}
              data-testid="admin-change-password-open"
              className="inline-flex items-center gap-2 text-sm text-charcoal hover:text-terracotta transition-colors"
            >
              <KeyRound size={16} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm text-wine hover:bg-blush/40 transition-colors"
            >
              <LogOut size={16} /> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-serif font-light text-4xl text-wine">Your dashboard</h1>
        <p className="text-charcoal/60 mt-2">Everyone who has raised their hand — coaching leads, subscribers, and messages.</p>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gold/30 border border-gold/30 mt-8" data-testid="admin-stats">
          {TABS.map((t) => (
            <div key={t.key} className="bg-cream p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-terracotta">{t.label}</p>
              <p className="font-serif text-5xl text-wine mt-3">{stats[t.key]}</p>
            </div>
          ))}
        </div>

        {/* Tabs + export */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-12 border-b border-gold/40">
          <div className="flex gap-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`pb-3 text-sm font-medium tracking-wide border-b-2 -mb-px transition-colors ${
                  tab === t.key ? "border-terracotta text-terracotta" : "border-transparent text-charcoal/60 hover:text-charcoal"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => exportCsv(tab)}
            data-testid={`admin-export-${tab}`}
            className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-5 py-2.5 text-sm font-medium hover:bg-wine transition-colors self-start sm:self-auto"
          >
            <Download size={16} /> Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto" data-testid="admin-table">
          {rows.length === 0 ? (
            <p className="py-16 text-center text-charcoal/50">Nothing here yet.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-[0.15em] text-charcoal/50 border-b border-gold/30">
                  <th className="py-3 pr-4 font-medium">Date</th>
                  <th className="py-3 pr-4 font-medium">Name</th>
                  <th className="py-3 pr-4 font-medium">Email</th>
                  <th className="py-3 pr-4 font-medium">
                    {tab === "subscribers" ? "Source" : tab === "contacts" ? "Message" : "Interest"}
                  </th>
                  {tab === "priority_list" && <th className="py-3 pr-4 font-medium">Origin</th>}
                  {(tab === "inquiries" || tab === "contacts" || tab === "priority_list") && (
                    <th className="py-3 pr-4 font-medium">Details</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gold/20 hover:bg-blush/20 transition-colors align-top">
                    <td className="py-4 pr-4 text-charcoal/70 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="py-4 pr-4 text-charcoal">{r.name || r.first_name || "—"}</td>
                    <td className="py-4 pr-4 text-charcoal/80">{r.email}</td>
                    <td className="py-4 pr-4 text-charcoal/80">
                      {tab === "subscribers"
                        ? r.source
                        : tab === "contacts"
                        ? r.message?.slice(0, 60) || ""
                        : r.interest || "—"}
                    </td>
                    {tab === "priority_list" && (
                      <td className="py-4 pr-4">
                        <span className="inline-block text-[0.65rem] uppercase tracking-[0.15em] text-terracotta border border-terracotta/40 rounded-full px-2 py-1 whitespace-nowrap">
                          {r.origin}
                        </span>
                      </td>
                    )}
                    {(tab === "inquiries" || tab === "contacts" || tab === "priority_list") && (
                      <td className="py-4 pr-4 text-charcoal/70 max-w-md">{r.message || "—"}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
      {showSetup && <SetupModal onClose={() => setShowSetup(false)} />}
    </main>
  );
}

function SetupModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/admin/system-status");
      setStatus(res.data);
    } catch (err) {
      toast.error("Could not load system status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!window.confirm("This will (re-)upload all 5 PDFs to Object Storage. Continue?")) return;
    setSeeding(true);
    try {
      const res = await adminApi.post("/admin/seed-pdfs");
      if (res.data.ok) toast.success(`Seeded ${res.data.seeded}/5 PDFs.`);
      else toast.error(`Only ${res.data.seeded}/5 PDFs succeeded. Check details.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Seed failed.");
    } finally {
      setSeeding(false);
    }
  };

  const provision = async () => {
    const mode = status?.stripe_mode;
    const label = mode === "live" ? "LIVE" : mode === "test" ? "TEST" : "UNKNOWN";
    if (!window.confirm(`This will create or update products/prices in the ${label} Stripe account. Continue?`)) return;
    setProvisioning(true);
    try {
      const res = await adminApi.post("/admin/provision-stripe");
      toast.success(`Provisioned Stripe (${res.data.stripe_mode}).`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Provisioning failed.");
    } finally {
      setProvisioning(false);
    }
  };

  const requiredKeys = ["tools_lifetime", "tools_monthly", "tools_yearly", "pdf_weekly_reset", "pdf_meal_planner", "pdf_family_center", "pdf_boundaries"];
  const missingKeys = status ? requiredKeys.filter(k => !status.stripe_active_lookup_keys.includes(k)) : [];

  const [cfg, setCfg] = useState(null);
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [keyForm, setKeyForm] = useState({ secret_key: "", publishable_key: "", account_id: "", webhook_secret: "" });
  const [savingKeys, setSavingKeys] = useState(false);

  const loadCfg = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/stripe-config");
      setCfg(res.data);
    } catch {
      setCfg(null);
    }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  const saveKeys = async (e) => {
    e.preventDefault();
    if (!keyForm.secret_key.startsWith("sk_live_") && !keyForm.secret_key.startsWith("sk_test_")) {
      toast.error("Secret key must start with sk_live_ or sk_test_.");
      return;
    }
    if (!window.confirm(`Save Stripe keys and switch to ${keyForm.secret_key.startsWith("sk_live_") ? "LIVE" : "TEST"} mode?`)) return;
    setSavingKeys(true);
    try {
      await adminApi.post("/admin/stripe-config", keyForm);
      toast.success("Stripe keys saved. Site is now running in the selected mode.");
      setShowKeyForm(false);
      setKeyForm({ secret_key: "", publishable_key: "", account_id: "", webhook_secret: "" });
      await loadCfg();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSavingKeys(false);
    }
  };

  const clearKeys = async () => {
    if (!window.confirm("Revert to environment-variable Stripe keys?")) return;
    try {
      await adminApi.delete("/admin/stripe-config");
      toast.success("Reverted to env-var keys.");
      await loadCfg();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Clear failed.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/50 flex items-center justify-center p-4" data-testid="admin-setup-modal">
      <div className="bg-cream w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8 border border-gold/40 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-charcoal/50 hover:text-charcoal" data-testid="admin-setup-close">
          <X size={20} />
        </button>
        <div className="flex items-center gap-3">
          <Settings size={22} className="text-terracotta" />
          <h2 className="font-serif font-light text-2xl text-wine">Production Setup</h2>
        </div>
        <p className="text-sm text-charcoal/70 mt-2 max-w-lg">
          Run these once after a fresh deployment. Everything is idempotent — safe to re-run any time.
        </p>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-charcoal/60"><Loader2 className="animate-spin mr-2" size={16} /> Loading status…</div>
        ) : status && (
          <div className="space-y-8 mt-8">
            {/* PDF SEEDING */}
            <section className="border border-gold/40 p-6" data-testid="setup-pdf-section">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl text-wine">Digital PDFs</h3>
                  <p className="mt-1 text-sm text-charcoal/70">
                    {status.pdfs_seeded_count}/{status.pdfs_total} products uploaded to Object Storage.
                  </p>
                </div>
                <StatusBadge good={status.pdfs_seeded_count === status.pdfs_total} label={status.pdfs_seeded_count === status.pdfs_total ? "Ready" : "Action needed"} />
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {status.pdfs.map(p => (
                  <li key={p.product_id} className="flex items-center gap-2">
                    {p.seeded ? <Check size={14} className="text-terracotta" /> : <AlertTriangle size={14} className="text-amber-600" />}
                    <span className={p.seeded ? "text-charcoal" : "text-charcoal/60"}>{p.name}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={seed}
                disabled={seeding}
                data-testid="setup-seed-pdfs"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-5 py-2.5 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
              >
                {seeding ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {seeding ? "Seeding…" : status.pdfs_seeded_count === 0 ? "Upload all PDFs" : "Re-upload PDFs"}
              </button>
            </section>

            {/* STRIPE PROVISIONING */}
            <section className="border border-gold/40 p-6" data-testid="setup-stripe-section">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-xl text-wine">Stripe catalog</h3>
                  <p className="mt-1 text-sm text-charcoal/70">
                    Current mode: <strong className={status.stripe_mode === "live" ? "text-terracotta" : "text-amber-700"}>{status.stripe_mode.toUpperCase()}</strong>
                    {status.stripe_mode === "test" && " — production should be LIVE."}
                  </p>
                  <p className="mt-1 text-sm text-charcoal/70">
                    {status.stripe_active_lookup_keys.length} active prices found. {missingKeys.length === 0 ? "All required lookup_keys present." : `Missing: ${missingKeys.join(", ")}`}
                  </p>
                </div>
                <StatusBadge good={status.stripe_mode === "live" && missingKeys.length === 0} label={status.stripe_mode !== "live" ? "Sandbox mode" : missingKeys.length === 0 ? "Ready" : "Action needed"} />
              </div>
              {status.stripe_mode !== "live" && (
                <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-medium">Live mode not detected.</p>
                  <p className="mt-1">Paste your live Stripe keys below to switch instantly — no redeploy needed. (Alternatively, update env vars in Emergent's deployment config and redeploy.)</p>
                </div>
              )}

              {/* CONFIGURE STRIPE KEYS DIRECTLY */}
              <div className="mt-6 border border-gold/40 p-5 bg-blush/10" data-testid="stripe-key-config">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-terracotta">Stripe Keys</p>
                    <p className="mt-2 text-sm text-charcoal/80">
                      {cfg?.configured
                        ? <>Using DB-stored keys · <strong className={cfg.mode === "live" ? "text-terracotta" : "text-amber-700"}>{cfg.mode?.toUpperCase()}</strong> · {cfg.secret_masked}</>
                        : "Using environment-variable keys."}
                    </p>
                    {cfg?.configured && cfg?.publishable_key && (
                      <p className="mt-1 text-xs text-charcoal/60">pk: {cfg.publishable_key.slice(0, 12)}… · webhook: {cfg.webhook_configured ? "set" : "not set"}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {cfg?.configured && (
                      <button onClick={clearKeys} data-testid="stripe-keys-clear" className="text-xs text-charcoal/60 hover:text-terracotta underline underline-offset-2">
                        Revert to env
                      </button>
                    )}
                    <button
                      onClick={() => setShowKeyForm(v => !v)}
                      data-testid="stripe-keys-toggle"
                      className="text-xs uppercase tracking-[0.15em] text-terracotta border border-terracotta/40 rounded-full px-3 py-1 hover:bg-terracotta hover:text-cream transition-colors"
                    >
                      {showKeyForm ? "Cancel" : cfg?.configured ? "Update keys" : "Configure live keys"}
                    </button>
                  </div>
                </div>

                {showKeyForm && (
                  <form onSubmit={saveKeys} className="mt-5 space-y-4" data-testid="stripe-key-form">
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-charcoal/60">Secret key (sk_live_… or sk_test_…) *</label>
                      <input
                        required
                        type="password"
                        value={keyForm.secret_key}
                        onChange={e => setKeyForm({ ...keyForm, secret_key: e.target.value })}
                        data-testid="input-secret-key"
                        placeholder="sk_live_..."
                        autoComplete="off"
                        className="w-full bg-cream border border-gold/40 p-2.5 mt-1.5 text-sm text-charcoal font-mono focus:outline-none focus:border-terracotta"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-[0.15em] text-charcoal/60">Publishable key</label>
                        <input
                          value={keyForm.publishable_key}
                          onChange={e => setKeyForm({ ...keyForm, publishable_key: e.target.value })}
                          data-testid="input-publishable-key"
                          placeholder="pk_live_..."
                          autoComplete="off"
                          className="w-full bg-cream border border-gold/40 p-2.5 mt-1.5 text-sm text-charcoal font-mono focus:outline-none focus:border-terracotta"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-[0.15em] text-charcoal/60">Account ID</label>
                        <input
                          value={keyForm.account_id}
                          onChange={e => setKeyForm({ ...keyForm, account_id: e.target.value })}
                          data-testid="input-account-id"
                          placeholder="acct_..."
                          autoComplete="off"
                          className="w-full bg-cream border border-gold/40 p-2.5 mt-1.5 text-sm text-charcoal font-mono focus:outline-none focus:border-terracotta"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.15em] text-charcoal/60">Webhook signing secret</label>
                      <input
                        type="password"
                        value={keyForm.webhook_secret}
                        onChange={e => setKeyForm({ ...keyForm, webhook_secret: e.target.value })}
                        data-testid="input-webhook-secret"
                        placeholder="whsec_..."
                        autoComplete="off"
                        className="w-full bg-cream border border-gold/40 p-2.5 mt-1.5 text-sm text-charcoal font-mono focus:outline-none focus:border-terracotta"
                      />
                      <p className="mt-1 text-xs text-charcoal/50">Optional — you can add this after creating the webhook endpoint in Stripe.</p>
                    </div>
                    <button
                      type="submit"
                      disabled={savingKeys}
                      data-testid="stripe-keys-save"
                      className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-2.5 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
                    >
                      {savingKeys ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      {savingKeys ? "Saving…" : "Save & switch mode"}
                    </button>
                  </form>
                )}
              </div>

              <button
                onClick={provision}
                disabled={provisioning}
                data-testid="setup-provision-stripe"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-5 py-2.5 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
              >
                {provisioning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {provisioning ? "Provisioning…" : missingKeys.length === 0 ? "Re-sync Stripe catalog" : "Provision Stripe products"}
              </button>
            </section>

            <NotificationsPanel />

            <button
              onClick={load}
              data-testid="setup-refresh"
              className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-terracotta"
            >
              <RefreshCw size={14} /> Refresh status
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ good, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-[0.15em] rounded-full px-3 py-1 border whitespace-nowrap ${good ? "text-terracotta border-terracotta/40 bg-blush/20" : "text-amber-700 border-amber-400/60 bg-amber-50"}`}>
      {good ? <Check size={11} /> : <AlertTriangle size={11} />} {label}
    </span>
  );
}

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.post("/auth/change-password", form);
      toast.success("Password updated.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/50 flex items-center justify-center p-6" data-testid="admin-password-modal">
      <div className="bg-cream w-full max-w-md p-8 border border-gold/40 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-charcoal/50 hover:text-charcoal" data-testid="admin-password-close">
          <X size={20} />
        </button>
        <h2 className="font-serif font-light text-2xl text-wine">Change password</h2>
        <form onSubmit={submit} className="space-y-6 mt-6" data-testid="admin-password-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Current password</label>
            <input
              required
              type="password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              data-testid="current-password-input"
              className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">New password (min 8 chars)</label>
            <input
              required
              type="password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="new-password-input"
              className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-password-submit"
            className="w-full rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NotificationsPanel() {
  const [recipients, setRecipients] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/notification-recipients");
      setRecipients(res.data.recipients || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = () => {
    const email = draft.trim().toLowerCase();
    if (!email.includes("@")) { toast.error("Please enter a valid email."); return; }
    if (recipients.includes(email)) { toast.error("Already added."); return; }
    setRecipients([...recipients, email]);
    setDraft("");
  };

  const remove = (email) => setRecipients(recipients.filter(r => r !== email));

  const save = async () => {
    if (recipients.length === 0) { toast.error("Add at least one email."); return; }
    setSaving(true);
    try {
      await adminApi.post("/admin/notification-recipients", { recipients });
      toast.success("Notification recipients saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await adminApi.post("/admin/test-notification");
      toast.success(`Test email sent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}. Check your inbox.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Test failed.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <section className="border border-gold/40 p-6" data-testid="setup-notifications-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-xl text-wine">Inquiry Notifications</h3>
          <p className="mt-1 text-sm text-charcoal/70">
            Emails that receive a notification every time someone submits an inquiry, priority list, or contact form.
          </p>
        </div>
      </div>
      <ul className="mt-4 space-y-2" data-testid="notification-recipients-list">
        {recipients.map(r => (
          <li key={r} className="flex items-center justify-between gap-2 bg-blush/20 border border-gold/30 rounded px-3 py-2">
            <span className="text-sm text-charcoal font-mono">{r}</span>
            <button onClick={() => remove(r)} data-testid={`remove-${r}`} className="text-xs text-terracotta hover:text-wine">
              <X size={14} />
            </button>
          </li>
        ))}
        {recipients.length === 0 && (
          <li className="text-sm text-charcoal/60 italic">No recipients configured. Add at least one below.</li>
        )}
      </ul>
      <div className="mt-4 flex gap-2">
        <input
          type="email"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === "Enter" && add()}
          data-testid="notification-recipient-input"
          placeholder="you@example.com"
          className="flex-1 bg-cream border border-gold/40 p-2 text-sm text-charcoal focus:outline-none focus:border-terracotta"
        />
        <button onClick={add} data-testid="notification-recipient-add" className="text-xs uppercase tracking-[0.15em] text-terracotta border border-terracotta/40 rounded-full px-3 py-1 hover:bg-terracotta hover:text-cream transition-colors">
          Add
        </button>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid="notification-save"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-5 py-2.5 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {saving ? "Saving…" : "Save recipients"}
        </button>
        <button
          onClick={sendTest}
          disabled={testing || recipients.length === 0}
          data-testid="notification-test"
          className="inline-flex items-center gap-2 rounded-full border border-terracotta/40 text-terracotta px-5 py-2.5 text-sm font-medium hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-40"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
          {testing ? "Sending…" : "Send test email"}
        </button>
      </div>
    </section>
  );
}

